import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { TaskStatuses } from "@/constants/status";

export const useUpdateTaskStatusMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message?: string }, Error, { id: string; status: TaskStatuses }>({
        mutationFn: async ({ id, status }) => {
            try {
                const res = await axiosInstance
                    .patch(`/tasks/status/${id}`, {
                        status,
                    })
                    .then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
        },
    });
};

export const useBulkUetdsRegisterMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message?: string }[], Error, { taskIds: string[] }>({
        mutationFn: async (payload) => {
            try {
                const res = await axiosInstance.post(`/tasks/uetds`, payload).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
        },
    });
};

export const useBulkUnassignPassengersFromTask = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ unassigned: boolean }, Error, { operationsIds: string[] }>({
        mutationFn: async ({ operationsIds }) => {
            try {
                const res = await axiosInstance.post(`/tasks/unassign`, { operationsIds }).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
        },
    });
};

export const useMovePassengerToTaskMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<
        { id: string; ok: true; message: string },
        Error,
        { passengerId: string; fromTaskId: string; toTaskId: string }
    >({
        mutationFn: async ({ passengerId, fromTaskId, toTaskId }) => {
            try {
                return await axiosInstance
                    .post("/tasks/assign", { fromTaskId, toTaskId, passengerId })
                    .then((res) => res.data);
            } catch (err) {
                const status = (err as AxiosError<{ message: string }>)?.response?.status;
                const serverMessage = (err as AxiosError<{ message: string }>)?.response?.data?.message;
                const message =
                    status === 404 || status === 400 ? serverMessage : "Yolcu taşınırken beklenmeyen bir hata oluştu.";

                Sentry.captureException(err);
                enqueueSnackbar(message, { variant: "error" });
                throw new Error(message);
            }
        },
        onSuccess: (data) => {
            enqueueSnackbar(data.message, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TASKS] });
        },
    });
};

export const useBulkUetdsCancelMutation = () => {
    const queryClient = useQueryClient();
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<{ id: string; ok: boolean; message?: string }[], Error, { taskIds: string[] }>({
        mutationFn: async (payload) => {
            try {
                const res = await axiosInstance.patch(`/tasks/uetds/cancel`, payload).then((res) => res.data);

                return res;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_TASKS],
            });
        },
    });
};
